import { useState, useEffect } from 'react'
import { Menu } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { SidebarContent } from '@/components/Sidebar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import ResultsContent from './components/ResultsContent'
import ResultsSidebar from './components/ResultsSidebar'
import { assessmentApi } from '@/utils/api'
import { Button } from '@/components/ui/button'

interface AssessmentResult {
  id: string
  date: string
  isOverall: boolean
  completedAt?: string
  attemptNumber?: number
}

interface AssessmentData {
  id: string
  status: string
  completedAt?: string
  scores?: Record<string, number>
  riskLevels?: Record<string, string>
  attemptNumber?: number
}

export default function ResultsPage() {
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const [selectedResult, setSelectedResult] = useState<string>('overall')
  const [assessmentResults, setAssessmentResults] = useState<AssessmentResult[]>([
    { id: 'overall', date: 'Overall Performance', isOverall: true },
  ])
  const [assessmentsData, setAssessmentsData] = useState<Record<string, AssessmentData>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchAssessments = async () => {
      const coupleId = (user as { coupleId?: string })?.coupleId
      if (!coupleId) {
        setLoading(false)
        return
      }

      try {
        // Use the history endpoint which returns the user's own attempts
        const response = await assessmentApi.assessmentHistory(coupleId)
        const data = (response as { data?: any[] }).data || (response as unknown as any[])

        if (Array.isArray(data)) {
          // Filter completed assessments (history is already sorted by most recent first)
          const completedAssessments = data
            .filter((assessment: any) => assessment.status === 'completed' && assessment.completedAt)

          if (completedAssessments.length === 0) {
            setAssessmentResults([])
            setAssessmentsData({})
            return
          }

          // Build results list
          const results: AssessmentResult[] = [
            { id: 'overall', date: 'Overall Performance', isOverall: true },
          ]

          // Build assessments data map
          const dataMap: Record<string, AssessmentData> = {}

          completedAssessments.forEach((assessment: any) => {
            const date = new Date(assessment.completedAt)
            const formattedDate = date.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }).replace(/\//g, '-')

            results.push({
              id: assessment.id,
              date: formattedDate,
              isOverall: false,
              completedAt: assessment.completedAt,
              attemptNumber: assessment.attemptNumber,
            })

            dataMap[assessment.id] = {
              id: assessment.id,
              status: assessment.status,
              completedAt: assessment.completedAt,
              scores: assessment.scores || {},
              riskLevels: assessment.riskLevels || {},
              attemptNumber: assessment.attemptNumber,
            }
          })

          setAssessmentResults(results)
          setAssessmentsData(dataMap)
        }
      } catch (error) {
        console.error('Failed to fetch assessments:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchAssessments()
  }, [user])

  return (
    <div className='min-h-screen w-full bg-[#EDEFF3]'>
      <div className='p-4 sm:p-[27px]'>
        <div className='bg-white rounded-[24px] min-h-[calc(100vh-54px)]'>
          {/* Header */}
          <div className='px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4'>
            <div className='flex items-center gap-4'>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant='ghost' size='icon' className='lg:hidden -ml-2'>
                    <Menu className='h-6 w-6' />
                    <span className='sr-only'>Toggle menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side='left' className='w-[280px] p-0'>
                  <SidebarContent organizationName={user?.coupleOrganizationName || 'IRIS Dashboard'} />
                </SheetContent>
              </Sheet>
              <h1 className='text-[28px] sm:text-[36px] lg:text-[40px] font-bold text-[#151515] leading-[1.4] sm:leading-[70px]'>Results</h1>
            </div>
            <div className='flex items-center gap-4 lg:gap-8'>
              <div className='flex items-center gap-3'>
                <div className='w-[48px] h-[47px] bg-[#434343] rounded-full flex items-center justify-center'>
                  <span className='text-[16px] font-bold text-white'>{initials}</span>
                </div>
                <span className='text-[24px] font-bold text-[#353535] hidden sm:block'>{displayName}</span>
              </div>
              <div className='w-[41px] h-[39px] bg-[#F7F8FA] rounded-full flex items-center justify-center cursor-pointer'>
                <svg width='4' height='18' viewBox='0 0 4 18' fill='#434343'>
                  <circle cx='2' cy='2' r='2' />
                  <circle cx='2' cy='9' r='2' />
                  <circle cx='2' cy='16' r='2' />
                </svg>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className='flex flex-row h-[calc(100vh-180px)]'>
            {/* Sidebar - Fixed, no scroll */}
            {assessmentResults.length > 0 && (
              <div className='hidden lg:flex flex-col w-[280px] px-[20px] py-4 flex-shrink-0'>
                <ResultsSidebar
                  results={assessmentResults}
                  selectedResult={selectedResult}
                  onSelectResult={setSelectedResult}
                />
              </div>
            )}

            {/* Main Content - Scrollable */}
            <div className='flex-1 flex flex-col overflow-hidden'>
              <div className='flex-1 overflow-y-auto px-4 sm:px-6 md:px-10 py-4 sm:py-6'>
                {loading ? (
                  <div className='flex items-center justify-center h-full'>
                    <p className='text-[#4e4d4d] text-[16px]'>Loading results...</p>
                  </div>
                ) : assessmentResults.length === 0 ? (
                  <div className='flex items-center justify-center h-full'>
                    <p className='text-[#4e4d4d] text-[16px]'>Complete your assessment to see results.</p>
                  </div>
                ) : (
                  <ResultsContent
                    selectedResult={selectedResult}
                    assessmentsData={assessmentsData}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
